import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Settings, Key, Eye, EyeOff, CheckCircle, XCircle, ExternalLink, Save, Trash2, AlertTriangle } from 'lucide-react';

export default function ApiConfigurations({ api }) {
  const { toast } = useToast();
  const [cnpjWsConfig, setCnpjWsConfig] = useState({
    configured: false,
    token_preview: '',
    created_at: null,
    updated_at: null
  });

  const [googlePlacesConfig, setGooglePlacesConfig] = useState({
    configured: false,
    token_preview: '',
    created_at: null,
    updated_at: null
  });

  const [googleSearchConfig, setGoogleSearchConfig] = useState({
    configured: false,
    api_key_preview: '',
    engine_id: '',
    created_at: null,
    updated_at: null
  });
  
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'cnpj', 'google_places', 'google_search'
  const [newToken, setNewToken] = useState('');
  const [newApiKey, setNewApiKey] = useState('');
  const [newEngineId, setNewEngineId] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      const [cnpjResponse, placesResponse, searchResponse] = await Promise.all([
        api.get('/api-configurations/cnpj-ws'),
        api.get('/api-configurations/google-places'),
        api.get('/api-configurations/google-search')
      ]);
      
      setCnpjWsConfig(cnpjResponse.data);
      setGooglePlacesConfig(placesResponse.data);
      setGoogleSearchConfig(searchResponse.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const handleSaveToken = async () => {
    if (!newToken.trim()) {
      toast({
        title: "Erro",
        description: "Token é obrigatório",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.post('/api-configurations/cnpj-ws', {
        token: newToken.trim()
      });

      toast({
        title: "Sucesso",
        description: response.data.message
      });
      setShowConfigModal(false);
      setNewToken('');
      setShowToken(false);
      fetchConfigurations();
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erro ao salvar configuração';
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteToken = async () => {
    if (!confirm('Tem certeza que deseja remover a configuração da API CNPJ.ws?')) {
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.delete('/api-configurations/cnpj-ws');
      toast({
        title: "Sucesso",
        description: response.data.message
      });
      fetchConfigurations();
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erro ao remover configuração';
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Configurações de APIs</h1>
          <p className="text-slate-600 mt-1">Configure as chaves de APIs para extração de dados reais</p>
        </div>
        <Settings className="w-8 h-8 text-slate-400" />
      </div>

      {/* CNPJ.ws Configuration */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="flex flex-wrap items-center gap-2">
                <Key className="w-5 h-5 shrink-0" />
                <span>CNPJ.ws API Comercial</span>
                {cnpjWsConfig.configured ? (
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Configurada
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Não Configurada
                  </Badge>
                )}
              </CardTitle>
              <p className="text-sm text-slate-600 mt-1">
                API para extração de dados reais de empresas brasileiras por CNAE, cidade e estado
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('https://www.cnpj.ws', '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Site Oficial
              </Button>
              {cnpjWsConfig.configured ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteToken}
                  disabled={loading}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remover
                </Button>
              ) : null}
              <Button
                size="sm"
                onClick={() => setShowConfigModal(true)}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                <Key className="w-4 h-4 mr-1" />
                {cnpjWsConfig.configured ? 'Atualizar Token' : 'Configurar API'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Configuration Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <div className="font-medium">
                  {cnpjWsConfig.configured ? (
                    <span className="text-green-600">Ativa</span>
                  ) : (
                    <span className="text-red-600">Inativa</span>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Token</Label>
                <div className="font-medium">
                  {cnpjWsConfig.token_preview || 'Não configurado'}
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Configurado em</Label>
                <div className="font-medium text-sm">
                  {formatDate(cnpjWsConfig.created_at)}
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Última atualização</Label>
                <div className="font-medium text-sm">
                  {formatDate(cnpjWsConfig.updated_at)}
                </div>
              </div>
            </div>

            {/* Features List */}
            <div className="mt-6">
              <Label className="text-sm font-medium">Funcionalidades disponíveis com CNPJ.ws:</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                <div className="flex items-center space-x-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Busca por CNAE (atividade econômica)</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Filtro por estado e cidade</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Dados reais de empresas brasileiras</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Informações completas (sócios, capital, etc.)</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Dados atualizados diariamente</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Filtro por porte da empresa</span>
                </div>
              </div>
            </div>

            {/* Warning if not configured */}
            {!cnpjWsConfig.configured && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800">API não configurada</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Sem a API CNPJ.ws configurada, o sistema usará dados simulados para extração de leads.
                      Para extrair dados reais de empresas brasileiras, configure sua chave da API comercial.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Configuration Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Key className="w-5 h-5" />
              <span>Configurar CNPJ.ws API</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="token">Token da API Comercial</Label>
              <div className="relative">
                <Input
                  id="token"
                  type={showToken ? "text" : "password"}
                  value={newToken}
                  onChange={(e) => setNewToken(e.target.value)}
                  placeholder="Cole aqui o token da API CNPJ.ws"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            
            <div className="text-sm text-slate-600 space-y-2">
              <p><strong>Como obter o token:</strong></p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Acesse <a href="https://www.cnpj.ws" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">cnpj.ws</a></li>
                <li>Faça seu cadastro e escolha um plano comercial</li>
                <li>Acesse sua conta e copie o token da API</li>
                <li>Cole o token no campo acima</li>
              </ol>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveToken} 
              disabled={loading || !newToken.trim()}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Configuração
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}